<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import BrowseSharedViewsDialog from '@/components/BrowseSharedViewsDialog.vue';
import { areaIdForRoute, routeCanSaveView } from '@/navigation/activeArea';
import { parseViewParam } from '@/composables/routedSavedView';
import { useAreaPicker } from '@/composables/useAreaPicker';
import { useIsPhone } from '@/composables/useIsPhone';
import ImportViewDialog from '@/layouts/frame/ImportViewDialog.vue';
import NewSavedViewDialog from '@/layouts/frame/NewSavedViewDialog.vue';
import PageMenu from '@/layouts/frame/PageMenu.vue';
import PhonePillRow from '@/layouts/frame/PhonePillRow.vue';
import SidebarToggle from '@/layouts/frame/SidebarToggle.vue';

/**
 * The 48px bar across the top of the main area. It names the list or page you
 * are on, and nothing else: the icon strip already shows the area.
 *
 * On a desktop the name is one menu, for example "Zones ▾", holding what the
 * sidebar holds: the area's record types, its pages and your saved views
 * there. With nowhere else to go, as in Players with no saved views, it is
 * plain text. Home has no name here.
 *
 * On a phone the sidebar's items become a row of pills in the bar's place, as
 * in Macro. In an area with saved views the row always shows, because it
 * ends in the Saved views and Shared views pills. Elsewhere, with nowhere
 * else to go, the bar shows the page's name instead, so the page still has a
 * title. Exactly one of the two shows. Home has no name, so a phone draws no
 * bar there: Home's own top row starts the page.
 *
 * The row's sheets open the saved-view dialogs, and the header holds them.
 * Importing a view goes to a page with no row, and a dialog inside the row
 * would vanish mid-fade.
 *
 * A page can put its own actions on the right through the
 * `#page-header-actions` target, and replace the name through
 * `#page-header-title`. No page does today. Neither target exists on a phone
 * while the pill row shows, or on Home.
 */

const route = useRoute();
const isPhone = useIsPhone();

const picker = useAreaPicker(
    () => areaIdForRoute(route),
    () => ({
        routeName: typeof route.name === 'string' ? route.name : '',
        path: route.path,
        entityId: typeof route.meta.entityId === 'string' ? route.meta.entityId : null,
        viewId: parseViewParam(route.query?.view),
    }),
);

// The header is in exactly one of these states. Home on a phone is none of
// them, so no bar is drawn there.
const showsPillRow = computed(() => isPhone.value && (picker.value.hasChoice || picker.value.hasSavedViews));
const showsPhoneLabel = computed(() => isPhone.value && !showsPillRow.value && picker.value.label !== '');
const showsDesktopMenu = computed(() => !isPhone.value && picker.value.label !== '');
// A desktop keeps the bar even on Home, for the sidebar toggle.
const showsBar = computed(() => !isPhone.value || showsPhoneLabel.value);

const canSaveView = computed(() => routeCanSaveView(route));
const newViewOpen = ref(false);
const browseSharedOpen = ref(false);
const importOpen = ref(false);
</script>

<template>
  <!-- A record page draws its own header, with its menu, counter and actions.
       The search page has its own title beside its search box, as in Macro. -->
  <template v-if="route.params.id === undefined && route.name !== 'search'">
    <PhonePillRow
      v-if="showsPillRow"
      :picker="picker"
      :can-save-view="canSaveView"
      @new-view="newViewOpen = true"
      @browse-shared="browseSharedOpen = true"
      @import-view="importOpen = true"
    />
    <header v-else-if="showsBar" class="flex h-12 shrink-0 items-center justify-between gap-3 px-4">
      <nav aria-label="Breadcrumb" class="flex min-w-0 items-center gap-0.5 text-sm">
        <SidebarToggle class="-ml-1.5 mr-1" />
        <div id="page-header-title" class="flex min-w-0 items-center">
          <span v-if="showsPhoneLabel" class="min-w-0 truncate font-medium" data-testid="header-label">{{ picker.label }}</span>
          <PageMenu v-else-if="showsDesktopMenu" :picker="picker" :label="picker.label" />
        </div>
      </nav>
      <div id="page-header-actions" class="flex items-center gap-1" />
    </header>
  </template>
  <!-- Outside the block above, so the Import dialog outlives its own move to the import page. -->
  <template v-if="isPhone">
    <NewSavedViewDialog v-if="canSaveView" v-model:open="newViewOpen" />
    <BrowseSharedViewsDialog v-if="canSaveView" v-model:open="browseSharedOpen" />
    <ImportViewDialog v-model:open="importOpen" />
  </template>
</template>
